/**
 * Shared trackBy functions for use with *ngFor loops.
 *
 * Use these to prevent Angular from rebuilding DOM nodes on every change
 * detection cycle when an array's contents shift. Without trackBy, Angular
 * has to recreate every <li>, <tr>, or <div> for *ngFor whenever the array
 * reference changes — even if 99% of items are unchanged. With trackBy, Angular
 * compares by the returned key and only touches DOM for actually-changed items.
 *
 * USAGE:
 *   <li *ngFor="let item of items; trackBy: trackById">...</li>
 *
 * Then in your component:
 *   import { trackById } from '../../core/utils/track-by.util';
 *   trackById = trackById;
 */

/** Track by `.id` (most common case). Falls back to index if id is missing. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function trackById(index: number, item: any): string | number {
  return item?.id ?? index;
}

/** Track by `.id_*` or alternative ID field name. */
export function trackByItemId<T extends { id?: string | number; idItem?: string | number; itemId?: string | number }>(
  index: number,
  item: T
): string | number {
  return item?.id ?? item?.idItem ?? item?.itemId ?? index;
}

/** Track by `.idSite` (used in admin views with site lists). */
export function trackBySiteId<T extends { idSite?: string | number; id?: string | number }>(
  index: number,
  item: T
): string | number {
  return item?.idSite ?? item?.id ?? index;
}

/** Track by `.name` when items don't have an id (e.g. dropdown options keyed by label). */
export function trackByName<T extends { name?: string; value?: string }>(index: number, item: T): string | number {
  return item?.name ?? item?.value ?? index;
}

/** Track a primitive value (string/number) — for *ngFor of string[] or number[]. */
export function trackByValue(index: number, item: string | number): string | number {
  return item ?? index;
}

/** Default index-only tracker. Last resort — same as no trackBy but keeps the API consistent. */
export function trackByIndex(index: number): number {
  return index;
}
